import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

// --- Styled Components ---

const PlayerContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: 55px; // Fixed height for the overall component
`;

const PlayButton = styled.button`
  background: #3249D7;
  border: none;
  border-radius: 50%;
  width: 55px;
  height: 55px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  flex-shrink: 0;
  z-index: 2;
  box-shadow: 0 3px 7px rgba(0, 0, 0, 0.2);
  transition: background-color 0.2s ease-in-out;

  &:disabled {
    background: #a5b4fc;
    cursor: not-allowed;
  }

  svg {
    width: 26px;
    height: 26px;
  }
`;

const VisualizerWrapper = styled.div`
  flex-grow: 1;
  height: 45px; /* Slightly smaller than button to create a subtle border effect */
  background: #ffffff;
  border: 2px solid #e9ecef;
  border-radius: 30px; /* Highly rounded to match the image */
  display: flex;
  align-items: center;
  padding: 0 20px 0 35px; /* Adjust padding to make space for the overlapping button */
  margin-left: -28px; /* Overlap with the button */
  box-shadow: inset 0 1px 3px rgba(0,0,0,0.05); /* Subtle inner shadow for depth */
`;

const Canvas = styled.canvas`
  width: 100%;
  height: 35px; /* Height of the actual canvas drawing area */
  display: block; /* Remove extra space below canvas */
`;

const HiddenAudio = styled.audio`
  display: none;
`;

// --- Icons (Unchanged) ---
const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
);
const PauseIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" /></svg>
);

// --- The WaveformPlayer Component ---

const WaveformPlayer = ({
  url,
  onAudioComplete,
  isAlreadyCompleted = false,
  allowReplay = false,
  disabled = false,
  barColor = '#3249D7', // Allow custom bar color
  staticBarColor = '#d1d5db', // Color for static waves
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isCompleted, setIsCompleted] = useState(isAlreadyCompleted);
  const [audioError, setAudioError] = useState(null);
  const [isBuffering, setIsBuffering] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const audioRef = useRef(null);
  const canvasRef = useRef(null);

  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const stallTimerRef = useRef(null);

  // Slow-connection guard: surface Retry instead of a frozen UI if the clip is
  // still buffering after this long.
  const STALL_HINT_MS = 12000;

  const clearStallTimer = () => {
    if (stallTimerRef.current) {
      clearTimeout(stallTimerRef.current);
      stallTimerRef.current = null;
    }
  };

  const armStallTimer = () => {
    clearStallTimer();
    stallTimerRef.current = setTimeout(() => {
      setAudioError(prev =>
        prev || 'Audio is taking longer than usual on your connection. Tap "Retry audio".'
      );
    }, STALL_HINT_MS);
  };

  // Re-fetch ONLY this audio clip. The <audio> src is the presigned URL that
  // points directly at the single audio object, so load() re-downloads just
  // that clip — it does NOT refetch questions or anything else. The WebAudio
  // source stays bound to the same element, so the waveform keeps working.
  const retryAudio = async () => {
    const el = audioRef.current;
    if (!el) return;
    clearStallTimer();
    setAudioError(null);
    setRetrying(true);
    setIsBuffering(true);
    try {
      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }
      el.load();
      armStallTimer();
      await el.play();
      setIsPlaying(true);
      setIsBuffering(false);
      clearStallTimer();
    } catch (e) {
      setIsPlaying(false);
      setIsBuffering(false);
      setAudioError('Could not load the audio. Please check your connection and tap "Retry audio".');
    } finally {
      setRetrying(false);
    }
  };

  useEffect(() => {
    setIsCompleted(isAlreadyCompleted);
  }, [isAlreadyCompleted]);
  
  useEffect(() => {
    // Reset playing state when URL changes, also draw static waves
    setIsPlaying(false);
    setAudioError(null);
    setIsBuffering(false);
    clearStallTimer();
    drawStaticWaves();
  }, [url]);

  // Clear any pending stall timer on unmount.
  useEffect(() => () => clearStallTimer(), []);

  const handleEnded = () => {
    setIsPlaying(false);
    setIsCompleted(true);
    if (onAudioComplete) {
      onAudioComplete();
    }
    drawStaticWaves(); // Draw static waves after completion
  };

  const handleError = () => {
    setIsPlaying(false);
    setIsBuffering(false);
    clearStallTimer();
    setAudioError('Could not load the audio. Please check your connection and tap "Retry audio".');
  };

  const handleWaiting = () => {
    setIsBuffering(true);
    armStallTimer();
  };

  const handlePlaying = () => {
    setIsBuffering(false);
    clearStallTimer();
  };

  const handleCanPlay = () => {
    setIsBuffering(false);
  };

  const togglePlay = () => {
    if (
      disabled ||
      (isCompleted && !allowReplay) ||
      isPlaying // Prevent clicking while playing
    ) return;

    if (!isPlaying) {
      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume();
      }
      setAudioError(null);
      setIsBuffering(true);
      armStallTimer();
      audioRef.current.play()
        .then(() => {
          setIsPlaying(true);
          setIsBuffering(false);
          clearStallTimer();
        })
        .catch(e => {
          // Slow/aborted load — surface inline Retry (re-fetches only this clip).
          setIsPlaying(false);
          setIsBuffering(false);
          clearStallTimer();
          setAudioError('Could not load the audio. Please check your connection and tap "Retry audio".');
        });
    }
  };

  // Function to draw static, subtle waves when not playing
  const drawStaticWaves = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { width, height } = canvas;

    ctx.clearRect(0, 0, width, height);
    const barWidth = 3;
    const gap = 2;
    const numBars = Math.floor(width / (barWidth + gap));
    const waveHeight = height * 0.2; // Static waves are shorter

    for (let i = 0; i < numBars; i++) {
      // Create a subtle, random wave pattern
      const heightVariation = Math.sin(i * 0.3) * (waveHeight * 0.5) + waveHeight;
      const y = (height - heightVariation) / 2;
      ctx.fillStyle = staticBarColor;
      ctx.fillRect(i * (barWidth + gap), y, barWidth, heightVariation);
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { width, height } = canvas;

    // Set canvas resolution for crisp drawing
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    // Initial draw of static waves
    drawStaticWaves();

    if (!audioRef.current) return;

    // --- Web Audio API Setup ---
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      sourceRef.current = audioContextRef.current.createMediaElementSource(audioRef.current);

      sourceRef.current.connect(analyserRef.current);
      analyserRef.current.connect(audioContextRef.current.destination);
    }
    const analyser = analyserRef.current;
    analyser.fftSize = 256;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    
    let animationFrameId;
    
const drawDynamicWaves = () => {
      animationFrameId = requestAnimationFrame(drawDynamicWaves);
      analyser.getByteFrequencyData(dataArray);

      if (!canvas) return;
      const { width, height } = canvas;
      ctx.clearRect(0, 0, width, height);
      const barWidth = 3;
      const gap = 2;
      const totalBarSpace = barWidth + gap;
      const numBars = Math.floor(width / totalBarSpace);
      
      for (let i = 0; i < numBars; i++) {
        // CHANGE 1: More aggressive stretch for width. Changed to 0.3
        const dataIndex = Math.floor((i / numBars) * (bufferLength * 0.3));
        
        // CHANGE 2: Taller bars for more impact. Removed the * 0.9
        const barHeight = (dataArray[dataIndex] / 255) * height;
        
        const x = i * totalBarSpace;
        const y = (height - barHeight) / 2;
        ctx.fillStyle = barColor;
        ctx.fillRect(x, y, barWidth, barHeight);
      }
    };

    if (isPlaying) {
      drawDynamicWaves();
    } else {
      drawStaticWaves(); // Ensure static waves are redrawn if playback stops
    }

    return () => {
      cancelAnimationFrame(animationFrameId);
      // Clean up Web Audio API nodes if component unmounts
      // (Optional, as context can often persist if other players might use it)
      // if (sourceRef.current) {
      //   sourceRef.current.disconnect();
      //   sourceRef.current = null;
      // }
      // if (analyserRef.current) {
      //   analyserRef.current.disconnect();
      //   analyserRef.current = null;
      // }
      // if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      //   audioContextRef.current.close();
      //   audioContextRef.current = null;
      // }
    };
  }, [isPlaying, url, barColor, staticBarColor]);


  return (
    <div style={{ width: '100%' }}>
      <PlayerContainer>
        <HiddenAudio
          ref={audioRef}
          src={url}
          preload="auto"
          onEnded={handleEnded}
          onError={handleError}
          onWaiting={handleWaiting}
          onStalled={handleWaiting}
          onPlaying={handlePlaying}
          onCanPlay={handleCanPlay}
          crossOrigin="anonymous"
        />
        <PlayButton
          onClick={togglePlay}
          disabled={
            disabled ||
            (isCompleted && !allowReplay) ||
            isPlaying // Disable while playing
          }
        >
          <PlayIcon />
        </PlayButton>
        <VisualizerWrapper>
          <Canvas ref={canvasRef} />
        </VisualizerWrapper>
      </PlayerContainer>

      {isBuffering && !audioError && (
        <div style={{ color: '#555', fontSize: '14px', marginTop: '6px' }}>
          Loading audio…
        </div>
      )}

      {audioError && (
        <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <span style={{ color: 'red', fontSize: '14px' }}>{audioError}</span>
          <button
            type="button"
            onClick={retryAudio}
            disabled={retrying}
            style={{
              padding: '6px 18px',
              borderRadius: '20px',
              border: '2px solid #3249D7',
              background: '#fff',
              color: '#3249D7',
              fontWeight: 600,
              fontSize: '14px',
              cursor: retrying ? 'not-allowed' : 'pointer',
              opacity: retrying ? 0.6 : 1,
            }}
          >
            {retrying ? 'Retrying…' : 'Retry audio'}
          </button>
        </div>
      )}
    </div>
  );
};

export default WaveformPlayer;